<template>
  <header class="ds-header">
    <div class="ds-header__inner">
      <button
        v-if="showBack"
        type="button"
        class="ds-header__back"
        aria-label="Back"
        @click="$emit('back')"
      >
        <span class="material-icons">arrow_back</span>
      </button>
      <div v-else-if="iconUrl" class="ds-header__icon">
        <img :src="iconUrl" :alt="iconAlt" />
      </div>
      <div class="ds-header__text">
        <h2 class="ds-header__title">{{ title }}</h2>
        <p v-if="subtitle" class="ds-header__subtitle">{{ subtitle }}</p>
      </div>
      <!-- No `btn-close-white`: app.scss already swaps in a white icon, and the
           modifier's invert() filter would turn that back to black. -->
      <button
        v-if="dismissible"
        type="button"
        class="btn-close ds-header__close"
        data-bs-dismiss="modal"
        aria-label="Close"
      ></button>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'DetailSheetHeader',
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    iconUrl: { type: String, default: '' },
    iconAlt: { type: String, default: '' },
    // Nested pages of a multi-page dialog show a back arrow where the icon
    // would otherwise sit, so the navigation lives in one predictable spot.
    showBack: { type: Boolean, default: false },
    dismissible: { type: Boolean, default: true },
  },
  emits: ['back'],
});
</script>

<style scoped>
/* A dark header with a yellow hairline underneath, rather than the old solid
   yellow slab: the brand accent stays, the title keeps the dialog's contrast. */
.ds-header {
  background: linear-gradient(180deg, var(--gray-dark) 0%, var(--dark) 100%);
  border-bottom: 2px solid var(--yellow);
}

.ds-header__inner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1rem 0.875rem;
}

.ds-header__icon,
.ds-header__back {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ds-header__icon {
  background: rgba(253, 223, 0, 0.14);
  border: 1px solid rgba(253, 223, 0, 0.35);
}

.ds-header__icon img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.ds-header__back {
  padding: 0;
  background: var(--gray);
  border: 1px solid var(--gray-border);
  color: #fff;
}

.ds-header__back:hover {
  background: var(--highlight);
  color: var(--yellow);
}

.ds-header__text {
  flex: 1 1 auto;
  min-width: 0;
}

.ds-header__title {
  margin: 0;
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.3;
  /* Long imported POI names must wrap rather than push the close button off. */
  overflow-wrap: anywhere;
}

.ds-header__subtitle {
  margin: 0.125rem 0 0;
  color: var(--text-muted);
  font-size: 0.8125rem;
  line-height: 1.3;
}

.ds-header__close {
  flex: 0 0 auto;
  align-self: flex-start;
  opacity: 0.65;
}

.ds-header__close:hover {
  opacity: 1;
}
</style>
